import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { controlToastState, Modal } from "risa-oasis-ui_v2";
import { commentRpaModalSave } from "../../../api/postCall/commentRpaModalSave";
import { useBusniessOfficeNotesContext } from "../../../context/busniessOfficeNotesContext";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { BadgeWithIconProps } from "../../../data-model/badgeWithIconProps";
import { AllCommentsProps } from "../../../data-model/busniessOfficeNote";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../enums/evBvWriteBackStatus";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { LocalStorageKeys } from "../../../enums/localStorageKeys";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import WarningIcon from "../../../svg/warningIcon";
import {
  checkIfEveryDocTypeIsUploaded,
  determineIfDocumentRpaShouldRun,
} from "../../../utils/determineIfDocumentRpaShouldRun";
import { getItemFromLocalStorage } from "../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { getCommentsToWriteFromAllComments } from "../../../utils/postCommentHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import BadgeDropdown from "../../badgeDropdown/badgeDropdown";
import CustomCheckBox from "../../customCheckBox/customCheckBox";
import CustomRadioButton from "../../customRadioButton/customRadioButton";

interface CommentRpaNewModalProps {
  onClose: () => void;
  onSave: () => void;
  wrtiteBackStatus: string;
  docRpaStatus: string;
  id: string;
  orderId?: string;
  writeBackReports?: AllCommentsProps[] | null;
  auth_status?: string;
  initialAuthStatus?: string;
}

const CommentRpaNewModal = (props: CommentRpaNewModalProps) => {
  const { data: boOptions } = useSelector(
    (state: RootState) => state.boOptions,
  );
  const defaultBadge = getItemFromLocalStorage(
    `${LocalStorageKeys.GLOBAL_BO_VALUE}_${props.id}`,
  );

  const [selectedProcessingMethod, setSelectedProcessingMethod] =
    useState<string>("");
  const [boStatusOptions, setBoStatusOptions] = useState<BadgeWithIconProps[]>(
    [],
  );
  const [selectedBadge, setSelectedBadge] = useState<BadgeWithIconProps | null>(
    defaultBadge || {
      id: "",
      text: "Select Status",
      bgColor: "#F5F5F5",
      textColor: "#0F0F0F",
    },
  );
  const { allCommentsForUI } = useBusniessOfficeNotesContext();

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const orgId = getOrgIdForFetchExternalWorklist();

  const shouldShowCheckboxes = useMemo(() => {
    if (
      props.wrtiteBackStatus === EVWriteBackStatus.NOT_INITIATED ||
      props.wrtiteBackStatus === EVWriteBackStatus.ERROR
    ) {
      return true;
    }
    return false;
  }, [props.wrtiteBackStatus, props.docRpaStatus]);

  const shouldIncludeWriteBackReports = useMemo(() => {
    return (
      props.wrtiteBackStatus === EVWriteBackStatus.NOT_INITIATED ||
      props.wrtiteBackStatus === EVWriteBackStatus.ERROR
    );
  }, [props.wrtiteBackStatus]);

  const handleSave = async () => {
    try {
      if (
        !checkIfEveryDocTypeIsUploaded(props.orderId ?? props.id) &&
        selectedProcessingMethod === "manual" &&
        props.docRpaStatus !== ""
      ) {
        return;
      }
      setIsLoading(true);

      const allPostComments = props?.writeBackReports
        ? getCommentsToWriteFromAllComments(props?.writeBackReports ?? [])
        : getCommentsToWriteFromAllComments(allCommentsForUI);

      const payload = {
        identifier: props.orderId || props.id,
        org_id: orgId,
        bo_status: selectedBadge?.id || null,
        overwrite_rpa_errors: true,
        write_back_reports:
          shouldIncludeWriteBackReports &&
          allPostComments &&
          allPostComments.length > 0
            ? ((allPostComments ?? []) as unknown as AllCommentsProps[])
            : null,
        ...(props.auth_status ? { auth_status: props.auth_status } : {}),
        payer_name: payerName,
      };
      await commentRpaModalSave(payload);
      props.onSave();
    } catch (error) {
      controlToastState("mark-as-completed-error");
    } finally {
      setIsLoading(false);
    }
  };

  const determineTextForWarningBanner = useMemo(() => {
    if (
      (props.wrtiteBackStatus === EVWriteBackStatus.NOT_INITIATED ||
        props.wrtiteBackStatus === EVWriteBackStatus.ERROR) &&
      (props.docRpaStatus === DocWriteBackStatus.NOT_INITIATED ||
        props.docRpaStatus === DocWriteBackStatus.ERROR) &&
      determineIfDocumentRpaShouldRun(props.orderId ?? props.id)
    ) {
      return " Comment RPA & Document RPA";
    }
    if (
      props.wrtiteBackStatus === EVWriteBackStatus.NOT_INITIATED ||
      props.wrtiteBackStatus === EVWriteBackStatus.ERROR
    ) {
      return "Comment RPA";
    }
    if (
      determineIfDocumentRpaShouldRun(props.orderId ?? props.id) &&
      (props.docRpaStatus === DocWriteBackStatus.NOT_INITIATED ||
        props.docRpaStatus === DocWriteBackStatus.ERROR)
    ) {
      return "Document RPA";
    }
    return "";
  }, [props.wrtiteBackStatus, props.docRpaStatus]);

  const shouldShowBothCheckboxes = useMemo(() => {
    return (
      (props.wrtiteBackStatus === EVWriteBackStatus.NOT_INITIATED ||
        props.wrtiteBackStatus === EVWriteBackStatus.ERROR) &&
      (props.docRpaStatus === DocWriteBackStatus.NOT_INITIATED ||
        props.docRpaStatus === DocWriteBackStatus.ERROR) &&
      determineIfDocumentRpaShouldRun(props.orderId ?? props.id)
    );
  }, [props.wrtiteBackStatus, props.docRpaStatus]);

  useEffect(() => {
    if (boOptions) {
      const boStatusOptions = boOptions.map((option) => ({
        id: option.value,
        text: option.label,
        bgColor: "#F5F5F5",
        textColor: "#0F0F0F",
      }));
      setBoStatusOptions(boStatusOptions);

      if (defaultBadge) {
        const matchingBadge = boStatusOptions.find(
          (option) => option.id === defaultBadge,
        );
        if (matchingBadge) {
          setSelectedBadge(matchingBadge);
        }
      }
    }
  }, [boOptions]);

  const handleCheckBoxValueChange = ({
    name,
    value,
  }: {
    name: string;
    value: boolean;
  }) => {
    setSelectedOptions(
      value
        ? [...selectedOptions, name]
        : selectedOptions.filter((option) => option !== name),
    );
  };

  const disableSave = useMemo(() => {
    if (isLoading) return true;
    if (shouldShowBothCheckboxes) {
      return selectedOptions.length !== 2;
    }
    if (shouldShowCheckboxes) {
      if (selectedProcessingMethod === "manual") {
        return selectedBadge === null;
      }
      return selectedOptions.length !== 1;
    }

    return false;
  }, [selectedOptions, isLoading, selectedBadge, selectedProcessingMethod]);

  return (
    <Modal
      dialogId={ModalId.COMMENT_RPA_MODAL_NEW}
      title="Mark as Completed"
      onClose={props.onClose}
      onCancel={props.onClose}
      onSave={handleSave}
      disableSave={
        disableSave ||
        selectedProcessingMethod === "" ||
        (!checkIfEveryDocTypeIsUploaded(props.orderId ?? props.id) &&
          selectedProcessingMethod === "manual" &&
          props.docRpaStatus !== "")
      }
      saveButtonText={isLoading ? "Saving..." : "Mark as Completed"}
      cancelText="Cancel"
    >
      <div className="comment-rpa-new-modal flex flex-col gap-4 px-2 py-3">
        {determineTextForWarningBanner && (
          <div className="gap flex flex-row items-center gap-[0.625rem] bg-[#FAE6E6] p-2">
            <WarningIcon outerFillColor="black" height="16" width="16" />
            <div className="text-sm font-normal text-primaryGray-1">
              You have not triggered the{" "}
              <span className="font-bold">
                {" "}
                {determineTextForWarningBanner}
              </span>
            </div>
          </div>
        )}
        <div className="flex flex-col items-start justify-center gap-5">
          {shouldShowCheckboxes ? (
            <>
              <div className="flex flex-col gap-2">
                <div className="text-sm font-normal text-primaryGray-1">
                  Please select the following to mark the order as completed
                </div>
                {shouldShowBothCheckboxes ? (
                  <>
                    <CustomCheckBox
                      id="comment-rpa"
                      label="Have you marked individual auth status"
                      isChecked={selectedOptions.includes("comment-rpa")}
                      onCheckBoxValueChange={(value) => {
                        handleCheckBoxValueChange(value);
                        apiWorkerManager.enqueueAndProcess({
                          patient_id: props.orderId ?? props.id,
                          order_id: props.orderId ?? props.id,
                          org_id: orgId,
                          event_name:
                            EventsName.COMMENT_RPA_MODAL_CHECKBOX_CHECKED,
                          user_id: user?.id ?? "",
                          user_email: user?.email ?? "",
                          additional_data: {
                            screen_name:
                              ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                            modal_name:
                              ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
                            checked_value: value.value,
                            payer_name: payerName,
                          },
                        });
                      }}
                      className="!mt-[3px] text-sm"
                    />
                    <CustomCheckBox
                      id="document-rpa"
                      label="Have you uploaded document"
                      isChecked={selectedOptions.includes("document-rpa")}
                      onCheckBoxValueChange={(value) => {
                        handleCheckBoxValueChange(value);
                        apiWorkerManager.enqueueAndProcess({
                          patient_id: props.orderId ?? props.id,
                          order_id: props.orderId ?? props.id,
                          org_id: orgId,
                          event_name:
                            EventsName.COMMENT_RPA_MODAL_CHECKBOX_CHECKED,
                          user_id: user?.id ?? "",
                          user_email: user?.email ?? "",
                          additional_data: {
                            screen_name:
                              ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                            modal_name:
                              ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
                            checked_value: value.value,
                            payer_name: payerName,
                          },
                        });
                      }}
                      className="!mt-[3px] text-sm"
                    />
                  </>
                ) : (
                  <CustomCheckBox
                    id="comment-rpa"
                    label="Have you marked individual auth status"
                    isChecked={selectedOptions.includes("comment-rpa")}
                    onCheckBoxValueChange={(value) => {
                      handleCheckBoxValueChange(value);
                      apiWorkerManager.enqueueAndProcess({
                        patient_id: props.orderId ?? props.id,
                        order_id: props.orderId ?? props.id,
                        org_id: orgId,
                        event_name:
                          EventsName.COMMENT_RPA_MODAL_CHECKBOX_CHECKED,
                        user_id: user?.id ?? "",
                        user_email: user?.email ?? "",
                        additional_data: {
                          screen_name:
                            ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                          modal_name:
                            ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
                          checked_value: value.value,
                          payer_name: payerName,
                        },
                      });
                    }}
                    className="!mt-[3px] text-sm"
                  />
                )}
              </div>
            </>
          ) : null}

          <div className="flex flex-col gap-2">
            <div className="text-sm font-normal text-primaryGray-1">
              How was this order processed
            </div>
            <div className="flex flex-col gap-2">
              <CustomRadioButton
                name="comment-rpa"
                value="manual"
                label="Manual"
                checked={selectedProcessingMethod === "manual"}
                onChange={() => {
                  setSelectedProcessingMethod("manual");
                  apiWorkerManager.enqueueAndProcess({
                    patient_id: props.orderId ?? props.id,
                    order_id: props.orderId ?? props.id,
                    org_id: orgId,
                    event_name:
                      EventsName.COMMENT_RPA_MODAL_RADIO_BUTTON_CLICKED,
                    user_id: user?.id ?? "",
                    user_email: user?.email ?? "",
                    additional_data: {
                      screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                      modal_name: ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
                      checked_value: "manual",
                      payer_name: payerName,
                    },
                  });
                }}
              />
              <CustomRadioButton
                name="comment-rpa"
                value="not_required"
                label="Not Required"
                checked={selectedProcessingMethod === "not_required"}
                onChange={() => {
                  setSelectedProcessingMethod("not_required");
                  apiWorkerManager.enqueueAndProcess({
                    patient_id: props.orderId ?? props.id,
                    order_id: props.orderId ?? props.id,
                    org_id: orgId,
                    event_name:
                      EventsName.COMMENT_RPA_MODAL_RADIO_BUTTON_CLICKED,
                    user_id: user?.id ?? "",
                    user_email: user?.email ?? "",
                    additional_data: {
                      screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                      modal_name: ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
                      checked_value: "not_required",
                      payer_name: payerName,
                    },
                  });
                }}
              />
              {selectedProcessingMethod === "manual" && (
                <div
                  className="flex flex-row items-center gap-2"
                  onClick={() => {
                    apiWorkerManager.enqueueAndProcess({
                      patient_id: props.orderId ?? props.id,
                      order_id: props.orderId ?? props.id,
                      org_id: orgId,
                      event_name: EventsName.COMMENT_RPA_MODAL_DROPDOWN_CLICKED,
                      user_id: user?.id ?? "",
                      user_email: user?.email ?? "",
                      additional_data: {
                        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                        modal_name: ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
                        default_value: selectedBadge?.id ?? "",
                        payer_name: payerName,
                      },
                    });
                  }}
                >
                  <div className="text-sm font-normal text-primaryGray-1">
                    Confirm the global BO value :{" "}
                  </div>
                  <BadgeDropdown
                    onDefaultBadgeClick={(id) => {}}
                    selectedBadge={selectedBadge}
                    badgeList={boStatusOptions
                      .sort((a, b) => a.id.localeCompare(b.id))
                      .filter((status) => status.id !== "")}
                    onClick={(badgeId) => {
                      apiWorkerManager.enqueueAndProcess({
                        patient_id: props.orderId ?? props.id,
                        order_id: props.orderId ?? props.id,
                        org_id: orgId,
                        event_name:
                          EventsName.COMMENT_RPA_MODAL_DROPDOWN_SELECTED,
                        user_id: user?.id ?? "",
                        user_email: user?.email ?? "",
                        additional_data: {
                          screen_name:
                            ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                          modal_name:
                            ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
                          default_value: selectedBadge?.id ?? "",
                          selected_value: badgeId,
                          payer_name: payerName,
                        },
                      });
                      const selectedBadgeObj = boStatusOptions.find(
                        (option) => option.id === badgeId,
                      );
                      setSelectedBadge(selectedBadgeObj || null);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {!checkIfEveryDocTypeIsUploaded(props.orderId ?? props.id) &&
          selectedProcessingMethod === "manual" &&
          props.docRpaStatus !== "" && (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2 rounded-md border border-secondaryYellow-4 bg-secondaryYellow-11 p-2 text-xs font-normal text-primaryGray-1">
                <WarningIcon height="16" width="16" />
                Please upload & save the document uploaded manually on OncoEMR
              </div>
            </div>
          )}
      </div>
    </Modal>
  );
};

export default CommentRpaNewModal;
